import { IX_TECHNOLOGIES, RO_TECH_SET, TECHNOLOGIES } from "@constants/global.constants";

import { getUniqueName, isFiniteNumber } from "@utils/appUtils";

import {
  ADJUSTMENT_TYPES,
  DEFAULT_ATMOSPHERIC_PRESSURE,
  DESIGN_FLOW_TYPES,
  DISCHARGE_PRESSURE_LEVELS,
  EDGE_COLOR_VARIANTS,
  EDGE_INSERTABLE_NODE_TYPES,
  EDGE_TYPES,
  HANDLE_IDS,
  HANDLE_TYPES,
  INITIAL_FEED_NODE_ID,
  INITIAL_PRODUCT_NODE_ID,
  MIX_INPUT_HANDLE_SET,
  NEW_NODE_DEFAULT_NAME_KEY_MAP,
  NODE_HANDLES,
  NODE_TYPES,
  RO_CONCENTRATE_PRESSURE_TYPES,
} from "./SystemDesign.constants";

import { Position } from "@xyflow/react";

/**
 * Returns true when the given node has at least one connected edge.
 */
export const isNodeConnected = (nodeId, edges) => edges.some(e => e.source === nodeId || e.target === nodeId);

export const getNodeById = (nodeId, nodes) => (nodeId ? (nodes.find(n => n.id === nodeId) ?? null) : null);

export const getEdgeById = (edgeId, edges) => (edgeId ? (edges.find(e => e.id === edgeId) ?? null) : null);

export const getHandleById = (handleId, handles) => (handleId ? (handles.find(h => h.id === handleId) ?? null) : null);

export const getNodeEdges = (nodeId, edges) => edges.filter(e => e.source === nodeId || e.target === nodeId);

export const getNodeOutgoingEdges = (nodeId, edges) => edges.filter(e => e.source === nodeId);

export const getNodeIncomingEdges = (nodeId, edges) => edges.filter(e => e.target === nodeId);

export const getEdgeIdSignature = (edges = []) =>
  edges
    .map(edge => edge?.id || "")
    .sort()
    .join(",");

export const hasSystemDesignChangeMapDiff = systemDesignChangeMap => {
  const addedNodeCount = Object.keys(systemDesignChangeMap?.add || {}).length;
  const deletedNodeCount = Object.keys(systemDesignChangeMap?.delete || {}).length;
  return addedNodeCount > 0 || deletedNodeCount > 0;
};

export const isGraphIdenticalByEdgeSignature = (edges = [], lastSavedEdgeIdSignature) => {
  if (!lastSavedEdgeIdSignature) return true;
  return getEdgeIdSignature(edges) === lastSavedEdgeIdSignature;
};

/**
 * Returns a map of feed node IDs to the ID of the first directly connected
 * downstream node only when that immediate node is a treatment.
 *
 * If the first connected node is missing or is not a treatment (for example,
 * Mix/Split/Adjustment), the feed maps to `null`.
 */
export const getFeedToDirectTreatmentMap = (nodes, edges) => {
  const feedNodeIds = getFeedNodes(nodes).map(node => node.id);

  return feedNodeIds.reduce((acc, feedNodeId) => {
    const firstConnectedNodeId = getNodeOutgoingEdges(feedNodeId, edges)?.[0]?.target;
    const firstConnectedNode = getNodeById(firstConnectedNodeId, nodes);

    acc[feedNodeId] = firstConnectedNode ? (isTreatmentNode(firstConnectedNode) ? firstConnectedNode.id : null) : null;
    return acc;
  }, {});
};

/** Returns a map of feed node IDs to the ID of the first treatment node found downstream. */
export const getFeedToTreatmentMap = (nodes, edges) => {
  const feedNodeIds = getFeedNodes(nodes).map(node => node.id);

  return feedNodeIds.reduce((acc, feedNodeId) => {
    const visited = new Set([feedNodeId]);
    const queue = getNodeOutgoingEdges(feedNodeId, edges).map(edge => edge.target);

    let firstTreatmentNodeId = null;

    while (queue.length) {
      const currentNodeId = queue.shift();
      if (!currentNodeId || visited.has(currentNodeId)) continue;

      visited.add(currentNodeId);
      const node = getNodeById(currentNodeId, nodes);
      if (!node) continue;

      if (isTreatmentNode(node)) {
        firstTreatmentNodeId = node.id;
        break;
      }

      const outgoing = getNodeOutgoingEdges(node.id, edges);
      outgoing.forEach(edge => queue.push(edge.target));
    }

    acc[feedNodeId] = firstTreatmentNodeId;
    return acc;
  }, {});
};

export const getNodeDischargeEdge = (nodeId, edges) =>
  edges.find(e => e.source === nodeId && e.type === EDGE_TYPES.DISCHARGE) || null;

export const getDischargeNodes = nodes => nodes.filter(n => n.type === NODE_TYPES.DISCHARGE);

export const getTreatmentNodes = nodes => nodes.filter(n => n.type === NODE_TYPES.TREATMENT);

export const getTreatmentNodesByTechnology = (nodes, technology) =>
  getTreatmentNodes(nodes).filter(node => node.data?.treatment === technology);

export const getRoTreatmentNodes = nodes => getTreatmentNodesByTechnology(nodes, TECHNOLOGIES.RO);

export const isTreatmentLimitExceeded = (nodes, maxTreatmentNodes, treatmentLimits) => {
  const treatmentNodes = getTreatmentNodes(nodes);
  if (treatmentNodes.length > maxTreatmentNodes) return true;

  const treatmentCounts = treatmentNodes.reduce(
    (counts, node) => {
      const treatment = node.data?.treatment;

      if (treatment === TECHNOLOGIES.UF) counts[TECHNOLOGIES.UF] += 1;
      else if (RO_TECH_SET.has(treatment)) counts[TECHNOLOGIES.RO] += 1;
      else if (IX_TECHNOLOGIES.includes(treatment)) counts[TECHNOLOGIES.IX] += 1;

      return counts;
    },
    {
      [TECHNOLOGIES.UF]: 0,
      [TECHNOLOGIES.RO]: 0,
      [TECHNOLOGIES.IX]: 0,
    },
  );

  return Object.entries(treatmentLimits).some(([technology, limit]) => treatmentCounts[technology] > limit);
};

/**
 * Checks if the project is a standalone/single technology project.
 * A project is standalone if it has exactly one treatment node.
 *
 * @param {Array} nodes - Array of system design nodes
 * @returns {boolean} - True if standalone (single technology), false if multitch/MLD
 */
export const isStandaloneProject = nodes => {
  const treatmentNodes = getTreatmentNodes(nodes);
  return treatmentNodes.length === 1;
};

/**
 * Checks if the project is a multi-technology/MLD (Multi-Loop Design) project.
 * A project is multitch if it has more than one treatment node.
 *
 * Note: this only checks node count. For the stricter MLD definition
 * (waste water routed into another treatment), use {@link isMldDesign}.
 *
 * @param {Array} nodes - Array of system design nodes
 * @returns {boolean} - True if multitch/MLD (multiple technologies), false if standalone
 */
export const isMultiTechProject = nodes => {
  const treatmentNodes = getTreatmentNodes(nodes);
  return treatmentNodes.length > 1;
};

/**
 * Returns the project technology configuration (standalone vs multitch/MLD).
 * Useful for understanding the scope and structure of the system design.
 *
 * @param {Array} nodes - Array of system design nodes
 * @returns {Object} - Configuration object with project type and treatment count
 *   - isStandalone: boolean
 *   - isMultiTech: boolean
 *   - treatmentCount: number
 *   - treatmentNodes: Array
 */
export const getProjectTechConfiguration = nodes => {
  const treatmentNodes = getTreatmentNodes(nodes);
  return {
    isStandalone: treatmentNodes.length === 1,
    isMultiTech: treatmentNodes.length > 1,
    treatmentCount: treatmentNodes.length,
    treatmentNodes,
  };
};

export const getAdjustmentNodes = nodes => nodes.filter(n => n.type === NODE_TYPES.ADJUSTMENT);

export const getMixNodes = nodes => nodes.filter(n => n.type === NODE_TYPES.MIX);

export const getFeedNodes = nodes => nodes.filter(n => n.type === NODE_TYPES.FEED);

export const getProductNodes = nodes => nodes.filter(n => n.type === NODE_TYPES.PRODUCT);

export const isDischargeNode = node => {
  if (typeof node === "string") return node === NODE_TYPES.DISCHARGE;
  return node.type === NODE_TYPES.DISCHARGE;
};

export const isTreatmentNode = node => {
  if (typeof node === "string") return node === NODE_TYPES.TREATMENT;
  return node.type === NODE_TYPES.TREATMENT;
};

export const isFeedNode = node => {
  if (typeof node === "string") return node === NODE_TYPES.FEED;
  return node.type === NODE_TYPES.FEED;
};

export const getNonFeedSourceStreamName = (sourceNode, incomingEdge, t, nodes = [], edges = []) => {
  const nodeName = sourceNode?.data?.name;

  if (sourceNode?.type === NODE_TYPES.SPLIT) {
    const isPrimarySplit = incomingEdge?.sourceHandle === HANDLE_IDS.SPLIT_RIGHT;
    const splitStreamNum = isPrimarySplit ? 1 : 2;
    return t("reports.streamNumOf", { num: splitStreamNum, name: nodeName });
  }

  if (sourceNode?.type === NODE_TYPES.DISCHARGE) {
    const upstreamDischargeEdge = getNodeIncomingEdges(sourceNode?.id, edges)?.find(
      edge => edge?.type === EDGE_TYPES.DISCHARGE,
    );
    const dischargeFromNode = getNodeById(upstreamDischargeEdge?.source, nodes);
    const dischargeSourceName = dischargeFromNode?.data?.name ?? nodeName;
    const isDischargeFromRo = RO_TECH_SET.has(sourceNode?.data?.dischargeFrom);
    return t(isDischargeFromRo ? "reports.concentrateOf" : "reports.wasteOf", { name: dischargeSourceName });
  }

  return t("reports.productOf", { name: nodeName });
};

export const getSourceStreamName = ({ targetNodeId, nodes, edges, t }) => {
  const incomingEdge = getNodeIncomingEdges(targetNodeId, edges)?.[0];
  const sourceNode = getNodeById(incomingEdge?.source, nodes);

  if (isFeedNode(sourceNode)) {
    return sourceNode?.data?.name || t("systemDesign.feed");
  }

  return getNonFeedSourceStreamName(sourceNode, incomingEdge, t, nodes, edges);
};

export const isProductNode = node => {
  if (typeof node === "string") return node === NODE_TYPES.PRODUCT;
  return node.type === NODE_TYPES.PRODUCT;
};

export const isHighPressureRoDischarge = nodeData => {
  const pressureType = nodeData?.dischargePressureType;
  const pressureValue = Number(nodeData?.dischargePressure);

  return (
    pressureType === RO_CONCENTRATE_PRESSURE_TYPES.CONCENTRATE ||
    (pressureType === RO_CONCENTRATE_PRESSURE_TYPES.SPECIFY && Number.isFinite(pressureValue) && pressureValue > 0)
  );
};

/**
 * Resolves upstream pressure level for an RO treatment node.
 *
 * Traversal rules:
 *  - Stops at Feed/Treatment nodes => Low
 *  - RO discharge configured as high pressure => High
 *  - Mix nodes recursively resolve from their two input handles
 *
 * Notes:
 *  - Intended for valid designs where Mix inputs are complete.
 *  - In valid designs, all resolved inputs for a Mix share the same pressure level.
 *
 * @param {string} nodeId - RO treatment node id (root node for backtracking).
 * @param {Array} nodes - Current React Flow node array.
 * @param {Array} edges - Current React Flow edge array.
 * @returns {"High" | "Low"}
 */
export const getRoNodePressureLevel = (nodeId, nodes = [], edges = []) => {
  const nodeMap = new Map(nodes.map(node => [node.id, node]));

  const incomingEdgesByTarget = edges.reduce((acc, edge) => {
    if (!acc.has(edge.target)) acc.set(edge.target, []);
    acc.get(edge.target).push(edge);
    return acc;
  }, new Map());

  const resolvePressureLevel = (currentNodeId, inProgressMixes, isRoot = false) => {
    const node = nodeMap.get(currentNodeId);

    if (!isRoot && (node.type === NODE_TYPES.TREATMENT || node.type === NODE_TYPES.FEED)) {
      return DISCHARGE_PRESSURE_LEVELS.LOW;
    }

    if (node.type === NODE_TYPES.DISCHARGE) {
      const isRODischarge = node?.data?.dischargeFrom === TECHNOLOGIES.RO;
      return isRODischarge && isHighPressureRoDischarge(node?.data)
        ? DISCHARGE_PRESSURE_LEVELS.HIGH
        : DISCHARGE_PRESSURE_LEVELS.LOW;
    }

    if (node.type === NODE_TYPES.MIX && inProgressMixes.has(currentNodeId)) {
      return DISCHARGE_PRESSURE_LEVELS.LOW;
    }

    const incomingEdges = incomingEdgesByTarget
      .get(currentNodeId)
      .filter(edge => node.type !== NODE_TYPES.MIX || MIX_INPUT_HANDLE_SET.has(edge.targetHandle));

    if (node.type === NODE_TYPES.MIX) {
      inProgressMixes.add(currentNodeId);
    }

    const firstIncomingEdge = incomingEdges[0];
    const pressureLevel = resolvePressureLevel(firstIncomingEdge.source, inProgressMixes, false);

    if (node.type === NODE_TYPES.MIX) {
      inProgressMixes.delete(currentNodeId);
    }

    return pressureLevel;
  };

  return resolvePressureLevel(nodeId, new Set(), true);
};

export const isFeedWaterDesign = type => type === DESIGN_FLOW_TYPES.FEED;

export const isProductWaterDesign = type => type === DESIGN_FLOW_TYPES.PRODUCT;

export const hasMixNode = nodes => nodes.some(node => node.type === NODE_TYPES.MIX);

export const isPrimaryFeed = id => id === INITIAL_FEED_NODE_ID;

export const isPrimaryProduct = id => id === INITIAL_PRODUCT_NODE_ID;

export const isPrimaryNode = id => isPrimaryFeed(id) || isPrimaryProduct(id);

export const normalizeEdgeFlow = flow => (isFiniteNumber(flow) ? flow : null);

/**
 * A node is eligible for mid-edge insertion only if it is not a terminal node
 * (feed/product) and neither of its main handles is already connected.
 */
export const isNodeEligibleForEdgeInsert = (node, edges) => {
  if (isFeedNode(node) || isProductNode(node)) return false;
  return (
    !isHandleConnected(node.id, HANDLE_IDS.MAIN_IN, edges) && !isHandleConnected(node.id, HANDLE_IDS.MAIN_OUT, edges)
  );
};

export const isHandleConnected = (nodeId, handleId, edges) =>
  edges.some(
    e => (e.source === nodeId && e.sourceHandle === handleId) || (e.target === nodeId && e.targetHandle === handleId),
  );

/**
 * Returns true when the given node has at least one MAIN-type edge.
 * Used to distinguish treatment nodes that only have their auto-managed
 * discharge edge (and are therefore still eligible for mid-edge insertion)
 * from nodes that are genuinely wired into the main process flow.
 */
export const hasMainEdge = (nodeId, edges) =>
  edges.some(e => (e.source === nodeId || e.target === nodeId) && e.type === EDGE_TYPES.MAIN);

/**
 * Returns true when the given edge is an auto-paired discharge edge —
 * a DISCHARGE-type edge whose source is a treatment node.
 * These edges are auto-managed and should not be valid drop or insert targets.
 */
export const isAutoPairedDischargeEdge = (edge, nodes) => {
  if (edge.type !== EDGE_TYPES.DISCHARGE) return false;
  const sourceNode = nodes.find(n => n.id === edge.source);
  return sourceNode?.type === NODE_TYPES.TREATMENT;
};

const getNewNodeDefaultNameKey = type => NEW_NODE_DEFAULT_NAME_KEY_MAP[type] ?? null;

/**
 * Derives a unique display name for a new node.
 */
export const resolveNewNodeName = (nodeConfig, nodes, t) => {
  const nameKey = getNewNodeDefaultNameKey(nodeConfig?.data?.adjustmentType ?? nodeConfig.type);
  const baseName = nodeConfig.type === NODE_TYPES.TREATMENT ? nodeConfig.data.treatment : nameKey ? t(nameKey) : "";
  const existingNames = nodes.map(n => n?.data?.name);
  return getUniqueName(existingNames, baseName);
};

/** Returns the id of the first handle matching `handleType` and `position`, or `null`. */
export const getHandleId = (handles, handleType, position) =>
  handles.find(h => h.handleType === handleType && h.position === position)?.id ?? null;

/** Returns true when `coords` has finite, non-zero x and y values. */
export const hasValidCoordinates = coords =>
  !!coords && Number.isFinite(coords.x) && Number.isFinite(coords.y) && (coords.x !== 0 || coords.y !== 0);

/**
 * Returns a unique node id in the form `<prefix>-<number>`.
 * The counter is derived from existing nodes of the same type,
 * so it remains consistent across sessions as long as nodes are passed in.
 */
/**
 * Checks whether a system design qualifies as an MLD.
 *
 * MLD rules:
 *  - A Mix Point alone does NOT make a design MLD.
 *  - A design is MLD when the **waste water** stream from any treatment
 *    (the DISCHARGE_OUT of a discharge node) can be traced, through any
 *    intermediate nodes (Mix, Split, Adjustment, etc.), to reach another
 *    treatment node's MAIN_IN.
 *  - Recirculation of RO permeate (treatment MAIN_OUT) back to another
 *    treatment via a Mix Point is multi-tech, NOT MLD, because it never
 *    originates from a DISCHARGE_OUT handle.
 *
 * @param {Array} nodes - React Flow nodes
 * @param {Array} edges - React Flow edges
 * @returns {boolean} - True if the design is MLD, false otherwise
 */
export const isMldDesign = (nodes, edges) => {
  const dischargeNodes = getDischargeNodes(nodes);
  return dischargeNodes.some(dischargeNode => isWasteWaterReachingTreatment(dischargeNode.id, nodes, edges));
};

/**
 * BFS forward from every DISCHARGE_OUT handle.
 * Returns true if `nodeId` is reachable via the waste/discharge recirculation path,
 * meaning it is NOT part of the clean main process flow.
 *
 * @param {string} nodeId - The node to test
 * @param {Array}  nodes
 * @param {Array}  edges
 * @returns {boolean}
 */
export const isNodeInWastePath = (nodeId, nodes, edges) => {
  const visited = new Set();
  const queue = edges.filter(edge => edge.sourceHandle === HANDLE_IDS.DISCHARGE_OUT).map(edge => edge.target);

  while (queue.length > 0) {
    const currentNodeId = queue.shift();
    if (!currentNodeId || visited.has(currentNodeId)) continue;
    visited.add(currentNodeId);

    if (currentNodeId === nodeId) return true;

    getNodeOutgoingEdges(currentNodeId, edges).forEach(edge => queue.push(edge.target));
  }

  return false;
};

/**
 * Returns the set of handle IDs from `handleIds` that currently have an incoming edge
 * on `nodeId`. Useful for determining which target (input) handles are in use.
 *
 * @param {string} nodeId
 * @param {Set<string>} handleIds - The handle IDs to test (e.g. MIX_INPUT_HANDLE_IDS)
 * @param {Array} edges
 * @returns {Set<string>}
 */
export const getIncomingConnectedHandles = (nodeId, handleIds, edges) =>
  new Set(edges.filter(e => e.target === nodeId && handleIds.has(e.targetHandle)).map(e => e.targetHandle));

/**
 * Returns the set of handle IDs from `handleIds` that currently have an outgoing edge
 * on `nodeId`. Useful for determining which source (output) handles are in use.
 *
 * @param {string} nodeId
 * @param {Set<string>} handleIds - The handle IDs to test (e.g. SPLIT_OUTPUT_HANDLE_IDS)
 * @param {Array} edges
 * @returns {Set<string>}
 */
export const getOutgoingConnectedHandles = (nodeId, handleIds, edges) =>
  new Set(edges.filter(e => e.source === nodeId && handleIds.has(e.sourceHandle)).map(e => e.sourceHandle));

/**
 * BFS from a discharge node's DISCHARGE_OUT handle.
 * Returns true if the waste water stream from that discharge node
 * can reach any treatment node downstream.
 *
 * @param {string} dischargeNodeId
 * @param {Array}  nodes
 * @param {Array}  edges
 * @returns {boolean}
 */
const isWasteWaterReachingTreatment = (dischargeNodeId, nodes, edges) => {
  const visited = new Set();
  const queue = edges
    .filter(edge => edge.source === dischargeNodeId && edge.sourceHandle === HANDLE_IDS.DISCHARGE_OUT)
    .map(edge => edge.target);

  while (queue.length > 0) {
    const currentNodeId = queue.shift();
    if (!currentNodeId || visited.has(currentNodeId)) continue;
    visited.add(currentNodeId);

    const currentNode = getNodeById(currentNodeId, nodes);
    if (!currentNode) continue;

    if (currentNode.type === NODE_TYPES.TREATMENT) return true;

    // Traverse through intermediate nodes (Mix, Split, Adjustment).
    // Do not continue through Discharge nodes to avoid false positives and cycles.
    if (currentNode.type !== NODE_TYPES.DISCHARGE) {
      getNodeOutgoingEdges(currentNodeId, edges).forEach(edge => queue.push(edge.target));
    }
  }

  return false;
};

export const createNodeId = (type, nodes = [], nodeData) => {
  const nodeTreatment = nodeData?.treatment;
  const adjustmentPrefix =
    type === NODE_TYPES.ADJUSTMENT ? (nodeData?.adjustmentType === ADJUSTMENT_TYPES.WQA ? "WQA" : "FP") : null;
  const dischargePrefix = type === NODE_TYPES.DISCHARGE ? `${nodeData?.dischargeFrom}-discharge` : null;
  const prefix = `${nodeTreatment ?? adjustmentPrefix ?? dischargePrefix ?? type}-`;
  const max = nodes
    .filter(n => n.id?.startsWith(prefix))
    .reduce((acc, n) => {
      const num = parseInt(n.id.slice(prefix.length), 10);
      return Number.isFinite(num) ? Math.max(acc, num) : acc;
    }, 0);
  return `${prefix}${max + 1}`;
};

export const dischargeNodeConfig = ({ nodes, treatmentPosition, treatment }) => {
  const dischargeNodeId = createNodeId(NODE_TYPES.DISCHARGE, nodes, { dischargeFrom: treatment });
  const dischargePosition = { x: treatmentPosition.x, y: treatmentPosition.y + 150 };
  return {
    id: dischargeNodeId,
    type: NODE_TYPES.DISCHARGE,
    position: dischargePosition,
    data: {
      dischargeFrom: treatment,
      ...(treatment === TECHNOLOGIES.RO
        ? {
            dischargePressureType: RO_CONCENTRATE_PRESSURE_TYPES.ATMOSPHERIC,
            dischargePressure: DEFAULT_ATMOSPHERIC_PRESSURE,
          }
        : null),
    },
    selectable: treatment === TECHNOLOGIES.RO,
  };
};

export const getMidPosition = (sourcePosition, targetPosition) => ({
  x: (sourcePosition.x + targetPosition.x) / 2,
  y: (sourcePosition.y + targetPosition.y) / 2,
});

/**
 * Returns a deterministic edge id derived from its four endpoint identifiers.
 * Determinism prevents duplicate edges when the same connection is created
 * more than once or when the graph is serialized and reloaded.
 */
export const createEdgeId = (source, sourceHandle, target, targetHandle) =>
  `edge-${source}-${sourceHandle}-${target}-${targetHandle}`;

export const dischargeEdgeConfig = ({ treatmentNodeId, dischargeId }) => ({
  id: createEdgeId(treatmentNodeId, HANDLE_IDS.DISCHARGE_OUT, dischargeId, HANDLE_IDS.DISCHARGE_IN),
  source: treatmentNodeId,
  sourceHandle: HANDLE_IDS.DISCHARGE_OUT,
  target: dischargeId,
  targetHandle: HANDLE_IDS.DISCHARGE_IN,
  type: EDGE_TYPES.DISCHARGE,
});

/**
 * Maps each edge-insertable node type to the `in` / `out` handle ids used
 * when the node is inserted mid-edge (via drop or drag-over).
 * Derived from NODE_HANDLES so it stays in sync with handle definitions.
 */
export const NODE_EDGE_INSERT_HANDLES = EDGE_INSERTABLE_NODE_TYPES.reduce((acc, type) => {
  const handles = NODE_HANDLES[type];
  acc[type] = {
    in: getHandleId(handles, HANDLE_TYPES.TARGET, Position.Left),
    out: getHandleId(handles, HANDLE_TYPES.SOURCE, Position.Right),
  };
  return acc;
}, {});

/**
 * Resolves the best available cursor position from a drop or drag-over event.
 * Prefers the drop event's client coordinates; falls back to the last recorded
 * drag-over position when the drop event coordinates are unavailable.
 * Returns `null` if neither source has valid coordinates.
 */
export const getCursorPositions = (event, dragOverFallback) => {
  const dropEventClientPosition = { x: event.clientX, y: event.clientY };
  const dragOverFallbackClientPosition = { x: dragOverFallback?.clientX, y: dragOverFallback?.clientY };

  const hasDropClientPosition = hasValidCoordinates(dropEventClientPosition);
  const hasFallbackClientPosition = hasValidCoordinates(dragOverFallbackClientPosition);

  if (!hasDropClientPosition && !hasFallbackClientPosition) return null;

  if (hasDropClientPosition) {
    return {
      client: dropEventClientPosition,
      source: "drop-event",
    };
  }

  return {
    client: dragOverFallbackClientPosition,
    source: "drag-over-fallback",
  };
};

/**
 * Returns the id of the first non-discharge edge under the cursor, or `null`.
 *
 * Uses `document.elementsFromPoint` against `react-flow__edge-interaction` —
 * an invisible wide `<path>` (`strokeWidth = EDGE_INTERACTION_WIDTH`) that
 * `BaseEdge` renders alongside each edge. Because this path lives in SVG/flow
 * space, the hit area scales automatically with zoom and pan, requiring no
 * manual coordinate transforms.
 *
 * Two CSS overrides in SystemDesignEdge.scss are required for this to work:
 *   - `pointer-events: stroke`  — overrides React Flow's inherited
 *     `visibleStroke`, which would exclude the opacity-0 interaction path.
 *   - `stroke: transparent`     — gives the path real stroke geometry;
 *     without an explicit color SVG defaults `stroke` to `none`, which
 *     produces no hittable area regardless of `strokeWidth`.
 *
 * @param {number} clientX - cursor X in screen/client pixels
 * @param {number} clientY - cursor Y in screen/client pixels
 * @param {Array}  edges   - current React Flow edge array
 * @param {Array}  nodes   - current React Flow node array
 */
export const getHoveredMainEdgeId = (clientX, clientY, edges, nodes) => {
  // Eligible drop targets: all edges except auto-paired discharge edges.
  const eligibleEdgeIds = new Set(edges.filter(e => !isAutoPairedDischargeEdge(e, nodes)).map(e => e.id));

  // elementsFromPoint returns every element under the cursor, top-to-bottom.
  const elements = document.elementsFromPoint(clientX, clientY);

  for (const el of elements) {
    // The interaction path has class "react-flow__edge-interaction".
    if (!el.classList.contains("react-flow__edge-interaction")) continue;

    // Walk up to the edge group wrapper which carries the data-id attribute.
    const edgeGroup = el.closest(".react-flow__edge");
    if (!edgeGroup) continue;

    const edgeId = edgeGroup.getAttribute("data-id");
    if (edgeId && eligibleEdgeIds.has(edgeId)) return edgeId;
  }

  return null;
};

/**
 * Looks up `targetEdgeId` in the graph and returns the edge together with the
 * midpoint between its source and target nodes — used to position a node
 * being inserted mid-edge. Returns `null` if the edge or either endpoint node
 * cannot be found.
 */
export const resolveEdgeInsertTarget = (targetEdgeId, nodes, edges) => {
  if (!targetEdgeId) return null;

  const targetEdge = getEdgeById(targetEdgeId, edges);
  if (!targetEdge) return null;

  const sourceNode = getNodeById(targetEdge.source, nodes);
  const targetNode = getNodeById(targetEdge.target, nodes);
  if (!sourceNode || !targetNode) return null;

  const midPosition = getMidPosition(sourceNode.position, targetNode.position);

  return { targetEdge, midPosition };
};

const DROP_ZONE_WIDTH = 140;
const DROP_ZONE_HEIGHT = 200;
const DROP_ZONE_OFFSET = 40;

const DROP_ZONE_DIRECTIONS = {
  VERTICAL: "vertical",
  HORIZONTAL: "horizontal",
};

const getDropZoneRect = (node, direction) => {
  const isVertical = direction === DROP_ZONE_DIRECTIONS.VERTICAL;
  const halfW = (node.measured?.width ?? 0) / 2;
  const halfH = (node.measured?.height ?? 0) / 2;

  const width = isVertical ? DROP_ZONE_WIDTH : DROP_ZONE_HEIGHT;
  const height = isVertical ? DROP_ZONE_HEIGHT : DROP_ZONE_WIDTH;

  // node.position is the center (nodeOrigin = [0.5, 0.5]).
  // Vertical (discharge): centered horizontally, starting below the node's bottom edge.
  // Horizontal (source): starting to the right of the node's right edge, centered vertically.
  const left = isVertical ? node.position.x - width / 2 : node.position.x + halfW + DROP_ZONE_OFFSET;
  const top = isVertical ? node.position.y + halfH + DROP_ZONE_OFFSET : node.position.y - height / 2;
  const right = left + width;
  const bottom = top + height;

  return { left, right, top, bottom };
};

const getDropZoneCenter = rect => ({ x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 });

const isPointInRect = (point, rect) =>
  point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;

export const getAutoConnectSourceHandle = nodeType => {
  const handles = NODE_HANDLES[nodeType];
  if (!handles) return null;

  if (isDischargeNode(nodeType)) {
    return getHandleById(HANDLE_IDS.DISCHARGE_OUT, handles);
  }

  // Prefer the right-side source handle for auto-connect placeholders, (e.g., Split uses SPLIT_RIGHT instead of MAIN_OUT).
  const rightSourceHandleId = getHandleId(handles, HANDLE_TYPES.SOURCE, Position.Right) ?? HANDLE_IDS.MAIN_OUT;
  return getHandleById(rightSourceHandleId, handles);
};

export const isAutoConnectEligible = (node, edges) => {
  const sourceHandle = getAutoConnectSourceHandle(node.type);
  if (!sourceHandle) return false;

  return !isHandleConnected(node.id, sourceHandle.id, edges);
};

const getNodeRect = node => {
  const halfW = (node.measured?.width ?? 0) / 2;
  const halfH = (node.measured?.height ?? 0) / 2;
  return {
    left: node.position.x - halfW,
    right: node.position.x + halfW,
    top: node.position.y - halfH,
    bottom: node.position.y + halfH,
  };
};

const doRectsOverlap = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

const isDropZoneOccupied = (rect, candidateNode, allNodes) =>
  allNodes.some(n => n.id !== candidateNode.id && doRectsOverlap(getNodeRect(n), rect));

const findConnectToNode = (flowPosition, candidateNodes, direction, allNodes) => {
  for (const node of candidateNodes) {
    const rect = getDropZoneRect(node, direction);
    if (!isPointInRect(flowPosition, rect)) continue;
    if (isDropZoneOccupied(rect, node, allNodes)) continue;

    return node.id;
  }

  return null;
};

const getDropZoneDischargePosition = node => getDropZoneCenter(getDropZoneRect(node, DROP_ZONE_DIRECTIONS.VERTICAL));

const getDropZoneSourcePosition = node => getDropZoneCenter(getDropZoneRect(node, DROP_ZONE_DIRECTIONS.HORIZONTAL));

const getDropZoneSourceNode = (flowPosition, nodes, edges) => {
  const eligibleNodes = nodes.filter(node => !isDischargeNode(node) && isAutoConnectEligible(node, edges));
  return findConnectToNode(flowPosition, eligibleNodes, DROP_ZONE_DIRECTIONS.HORIZONTAL, nodes);
};

const getDropZoneDischargeNode = (flowPosition, nodes, edges) => {
  const eligibleDischargeNodes = nodes.filter(node => isDischargeNode(node) && isAutoConnectEligible(node, edges));
  return findConnectToNode(flowPosition, eligibleDischargeNodes, DROP_ZONE_DIRECTIONS.VERTICAL, nodes);
};

export const getAutoConnectTarget = (flowPosition, nodes, edges) =>
  getDropZoneDischargeNode(flowPosition, nodes, edges) ?? getDropZoneSourceNode(flowPosition, nodes, edges);

export const getAutoConnectDropPosition = (node, applyOffset = false) => {
  if (isDischargeNode(node)) {
    const { x, y } = getDropZoneDischargePosition(node);
    return { x: applyOffset ? x + 100 : x, y };
  }
  const { x, y } = getDropZoneSourcePosition(node);
  return { x: applyOffset ? x + 50 : x, y };
};

// Ordering used to sort Split outputs: right-most output first, then top, then bottom.
const SPLIT_HANDLE_ORDER = {
  [HANDLE_IDS.SPLIT_RIGHT]: 0,
  [HANDLE_IDS.SPLIT_TOP]: 1,
  [HANDLE_IDS.SPLIT_BOTTOM]: 2,
};

const hasDirectedPath = (startId, targetId, outgoingEdges, ignoredEdgeId = null) => {
  if (!startId || !targetId) return false;

  const visited = new Set();
  const queue = [startId];

  while (queue.length > 0) {
    const currentId = queue.shift();
    if (!currentId || visited.has(currentId)) continue;
    if (currentId === targetId) return true;

    visited.add(currentId);

    for (const edge of outgoingEdges.get(currentId) ?? []) {
      if (ignoredEdgeId && edge.id === ignoredEdgeId) continue;
      if (!visited.has(edge.target)) queue.push(edge.target);
    }
  }

  return false;
};

/**
 * Assigns a sequential stream number to every edge using a two-phase tier approach:
 *
 * For each "tier" of start nodes (Feed nodes initially, recycle/discharge targets later):
 *   Phase 1 — BFS main-flow edges only (all levels until no more main edges):
 *     Numbers are assigned left → right by source node X position per BFS level.
 *     Split outputs from the same source are further sorted RIGHT → TOP → BOTTOM.
 *     Mix nodes are gated until ALL their incoming edges have been numbered.
 *   Phase 2 — All discharge edges collected during Phase 1:
 *     Numbered after ALL main edges in this tier, sorted left → right by source X.
 *     Discharge targets that have outgoing edges become start nodes for the next tier.
 *
 * This ensures the full main-flow spine is numbered first (1, 2, 3 …), then all
 * discharge branches (… 5, 6, 7), then any recycle/secondary branches (… 8, 9, 10).
 *
 * @param {Node[]} nodes  - React Flow node array
 * @param {Edge[]} edges  - React Flow edge array
 * @returns {Edge[]} A new array with `data.edgeNumber` set on each edge.
 *                   Disconnected edges receive `edgeNumber: null`.
 */
export const assignStreamNumbers = (nodes, edges) => {
  // ── Pre-computation ─────────────────────────────────────────────────────────
  const nodeById = new Map(nodes.map(n => [n.id, n]));
  const outgoingEdges = new Map();
  const incomingEdgeCount = new Map();
  const incomingAssigned = new Map();

  const isRecirculationIntoMix = edge => {
    const targetNode = nodeById.get(edge.target);
    if (targetNode?.type !== NODE_TYPES.MIX) return false;

    // A feedback edge into Mix is one where Mix can already reach the source
    // through other directed edges (cycle/back-edge), independent of node layout.
    return hasDirectedPath(edge.target, edge.source, outgoingEdges, edge.id);
  };

  for (const edge of edges) {
    if (!outgoingEdges.has(edge.source)) outgoingEdges.set(edge.source, []);
    outgoingEdges.get(edge.source).push(edge);

    incomingAssigned.set(edge.target, 0);
  }

  for (const edge of edges) {
    // Do not gate Mix progression on feedback recycle inputs.
    if (!isRecirculationIntoMix(edge)) {
      incomingEdgeCount.set(edge.target, (incomingEdgeCount.get(edge.target) ?? 0) + 1);
    }
  }

  const streamNumById = new Map();
  const visited = new Set();
  let streamNum = 0;

  const sortBySourceX = edgeList =>
    [...edgeList].sort((a, b) => {
      const xA = nodeById.get(a.source)?.position?.x ?? 0;
      const xB = nodeById.get(b.source)?.position?.x ?? 0;
      if (xA !== xB) return xA - xB;
      return (SPLIT_HANDLE_ORDER[a.sourceHandle] ?? 0) - (SPLIT_HANDLE_ORDER[b.sourceHandle] ?? 0);
    });

  const tryAdvance = (targetId, nextFrontier) => {
    if (visited.has(targetId)) return;
    const isMix = nodeById.get(targetId)?.type === NODE_TYPES.MIX;
    const assigned = incomingAssigned.get(targetId) ?? 0;
    const total = incomingEdgeCount.get(targetId) ?? 1;
    if (isMix && assigned < total) return; // gate: wait for all inputs
    visited.add(targetId);
    nextFrontier.push(targetId);
  };

  // ── Two-phase tier processing ────────────────────────────────────────────────
  // Returns the next tier's start nodes (discharge targets with outgoing edges).
  const processTier = startIds => {
    let mainFrontier = [...startIds];
    startIds.forEach(id => visited.add(id));

    const collectedDischargeEdges = [];

    // Phase 1: BFS main edges only across all levels in this tier
    while (mainFrontier.length > 0) {
      const mainEdgesThisLevel = [];
      const dischargeEdgesThisLevel = [];

      for (const nodeId of mainFrontier) {
        for (const edge of outgoingEdges.get(nodeId) ?? []) {
          (edge.type === EDGE_TYPES.DISCHARGE ? dischargeEdgesThisLevel : mainEdgesThisLevel).push(edge);
        }
      }

      // Collect discharges for Phase 2 — don't number them yet
      collectedDischargeEdges.push(...dischargeEdgesThisLevel);

      const nextMainFrontier = [];
      for (const edge of sortBySourceX(mainEdgesThisLevel)) {
        streamNumById.set(edge.id, ++streamNum);
        incomingAssigned.set(edge.target, (incomingAssigned.get(edge.target) ?? 0) + 1);
        tryAdvance(edge.target, nextMainFrontier);
      }

      mainFrontier = nextMainFrontier;
    }

    // Phase 2: Number all discharge edges from this tier, sorted left → right
    const nextTierIds = [];
    for (const edge of sortBySourceX(collectedDischargeEdges)) {
      streamNumById.set(edge.id, ++streamNum);
      incomingAssigned.set(edge.target, (incomingAssigned.get(edge.target) ?? 0) + 1);
      // Discharge targets with outgoing edges (e.g. recycle Waste → next tech) seed the next tier
      if (outgoingEdges.has(edge.target) && !visited.has(edge.target)) {
        visited.add(edge.target);
        nextTierIds.push(edge.target);
      }
    }

    return nextTierIds;
  };

  // Seed with Feed nodes, then iterate through tiers
  let tier = nodes.filter(n => n.type === NODE_TYPES.FEED).map(n => n.id);
  while (tier.length > 0) {
    tier = processTier(tier);
  }

  // ── Return edges with edgeNumber written into data (drives EdgeLabel display) ─
  return edges.map(edge => {
    const num = streamNumById.get(edge.id) ?? null;
    return { ...edge, data: { ...edge.data, edgeNumber: num } };
  });
};

/**
 * Assigns a sequential `nodeOrder` to every node using the same two-phase tier
 * approach as `assignStreamNumbers`:
 *
 *   Tier 1 starts from Feed nodes (sorted left → right).
 *   Phase 1 — BFS main-flow only:  nodes are sorted by (x, y) as they advance
 *             to each frontier level and assigned order there.
 *   Phase 2 — Discharge targets:   numbered after the full main spine, sorted
 *             left → right (then top → bottom). Targets with outgoing edges
 *             seed the next tier.
 *
 * Written to `node.nodeOrder` (top-level, not inside `data`).  Unreachable nodes
 * receive `null`.
 *
 * @param {Node[]} nodes
 * @param {Edge[]} edges
 * @returns {Node[]} New array with top-level `nodeOrder` set on each node, sorted ascending.
 */
export const assignNodeOrders = (nodes, edges) => {
  const nodeById = new Map(nodes.map(n => [n.id, n]));
  const outgoingEdges = new Map();
  const incomingEdgeCount = new Map();
  const incomingAssigned = new Map();

  const isRecirculationIntoMix = edge => {
    const targetNode = nodeById.get(edge.target);
    if (targetNode?.type !== NODE_TYPES.MIX) return false;

    // Keep node ordering and stream numbering aligned via topology-only detection.
    return hasDirectedPath(edge.target, edge.source, outgoingEdges, edge.id);
  };

  for (const edge of edges) {
    if (!outgoingEdges.has(edge.source)) outgoingEdges.set(edge.source, []);
    outgoingEdges.get(edge.source).push(edge);

    incomingAssigned.set(edge.target, 0);
  }

  for (const edge of edges) {
    // Keep node ordering aligned with stream numbering around recycle loops.
    if (!isRecirculationIntoMix(edge)) {
      incomingEdgeCount.set(edge.target, (incomingEdgeCount.get(edge.target) ?? 0) + 1);
    }
  }

  const nodeOrderById = new Map();
  const visited = new Set();
  let nodeOrder = 0;

  const sortByPosition = nodeIds =>
    [...nodeIds].sort((a, b) => {
      const nA = nodeById.get(a);
      const nB = nodeById.get(b);
      const xDiff = (nA?.position?.x ?? 0) - (nB?.position?.x ?? 0);
      if (xDiff !== 0) return xDiff;
      return (nA?.position?.y ?? 0) - (nB?.position?.y ?? 0);
    });

  const tryAdvanceMain = (targetId, nextFrontier) => {
    if (visited.has(targetId)) return;
    const isMix = nodeById.get(targetId)?.type === NODE_TYPES.MIX;
    const assigned = incomingAssigned.get(targetId) ?? 0;
    const total = incomingEdgeCount.get(targetId) ?? 1;
    if (isMix && assigned < total) return; // gate: wait for all inputs
    visited.add(targetId);
    nextFrontier.push(targetId);
  };

  // startIds already have nodeOrder assigned and are marked visited
  const processTier = startIds => {
    let mainFrontier = [...startIds];
    const dischargeTargetSet = new Set();

    // Phase 1: BFS main edges — sort each frontier level by position before assigning order
    while (mainFrontier.length > 0) {
      const mainEdgesThisLevel = [];
      const dischargeEdgesThisLevel = [];

      for (const nodeId of mainFrontier) {
        for (const edge of outgoingEdges.get(nodeId) ?? []) {
          (edge.type === EDGE_TYPES.DISCHARGE ? dischargeEdgesThisLevel : mainEdgesThisLevel).push(edge);
        }
      }

      for (const edge of dischargeEdgesThisLevel) {
        if (!visited.has(edge.target)) dischargeTargetSet.add(edge.target);
      }

      const nextMainFrontier = [];
      for (const edge of mainEdgesThisLevel) {
        incomingAssigned.set(edge.target, (incomingAssigned.get(edge.target) ?? 0) + 1);
        tryAdvanceMain(edge.target, nextMainFrontier);
      }

      const sorted = sortByPosition(nextMainFrontier);
      sorted.forEach(id => nodeOrderById.set(id, ++nodeOrder));
      mainFrontier = sorted;
    }

    // Phase 2: Discharge targets sorted by position
    const nextTierIds = [];
    for (const id of sortByPosition([...dischargeTargetSet])) {
      visited.add(id);
      nodeOrderById.set(id, ++nodeOrder);
      if (outgoingEdges.has(id)) nextTierIds.push(id);
    }

    return nextTierIds;
  };

  // Seed with Feed nodes — assign their order first, then BFS from them
  const sortedFeedIds = sortByPosition(nodes.filter(n => n.type === NODE_TYPES.FEED).map(n => n.id));
  sortedFeedIds.forEach(id => {
    visited.add(id);
    nodeOrderById.set(id, ++nodeOrder);
  });

  let tier = sortedFeedIds;
  while (tier.length > 0) {
    tier = processTier(tier);
  }

  return nodes
    .map(node => ({ ...node, nodeOrder: nodeOrderById.get(node.id) ?? null }))
    .sort((a, b) => (a.nodeOrder ?? Infinity) - (b.nodeOrder ?? Infinity));
};

const RO_TREATMENTS = [TECHNOLOGIES.RO, TECHNOLOGIES.CCRO, TECHNOLOGIES.ROSC];

const getTreatmentEdgeVariant = (treatment, isDischarge) => {
  if (treatment === TECHNOLOGIES.UF) return isDischarge ? EDGE_COLOR_VARIANTS.UF_DISCHARGE : EDGE_COLOR_VARIANTS.UF;
  if (RO_TREATMENTS.includes(treatment)) return isDischarge ? EDGE_COLOR_VARIANTS.RO_DISCHARGE : EDGE_COLOR_VARIANTS.RO;
  if (IX_TECHNOLOGIES.includes(treatment))
    return isDischarge ? EDGE_COLOR_VARIANTS.IX_DISCHARGE : EDGE_COLOR_VARIANTS.IX;
  return EDGE_COLOR_VARIANTS.FEED;
};

/**
 * Resolves the edge color variant for a given source node by walking upstream
 * through passthrough nodes (Adjustment, Split) until a source with a defined
 * color (Feed, Treatment, Mix) is reached.
 *
 * @param {string}  sourceId   - ID of the edge's source node
 * @param {boolean} isDischarge - true when the edge is a discharge (2nd output) edge
 * @param {Array}   nodes      - React Flow nodes array
 * @param {Array}   edges      - React Flow edges array
 * @param {Set}     [visited]  - cycle guard (internal)
 * @returns {string} One of EDGE_COLOR_VARIANTS values
 */
export const resolveEdgeColorVariant = (sourceId, isDischarge, nodes, edges, visited = new Set()) => {
  if (!sourceId || visited.has(sourceId)) return EDGE_COLOR_VARIANTS.FEED;
  visited.add(sourceId);

  const sourceNode = getNodeById(sourceId, nodes);
  if (!sourceNode) return EDGE_COLOR_VARIANTS.FEED;

  switch (sourceNode.type) {
    case NODE_TYPES.FEED:
      return EDGE_COLOR_VARIANTS.FEED;
    case NODE_TYPES.TREATMENT:
      return getTreatmentEdgeVariant(sourceNode.data?.treatment, isDischarge);
    case NODE_TYPES.MIX:
      return EDGE_COLOR_VARIANTS.MIX;
    case NODE_TYPES.ADJUSTMENT:
    case NODE_TYPES.SPLIT: {
      const incomingEdge = edges.find(e => e.target === sourceId);
      if (!incomingEdge) return EDGE_COLOR_VARIANTS.FEED;
      return resolveEdgeColorVariant(incomingEdge.source, false, nodes, edges, visited);
    }
    case NODE_TYPES.DISCHARGE: {
      const incomingEdge = edges.find(e => e.target === sourceId && e.type === EDGE_TYPES.DISCHARGE);
      if (!incomingEdge) return EDGE_COLOR_VARIANTS.FEED;
      return resolveEdgeColorVariant(incomingEdge.source, true, nodes, edges, visited);
    }
    default:
      return EDGE_COLOR_VARIANTS.FEED;
  }
};

/**
 * Returns treatment node IDs reachable by repeatedly following the downstream
 * waste branch starting from a treatment node ID.
 *
 * Traversal rule:
 * - From treatment nodes: follow only waste/discharge outgoing edges.
 * - From non-treatment nodes: follow all outgoing edges.
 */
export const getDownstreamWasteNodeIds = (nodes, edges, treatmentNodeId) => {
  const nodeById = new Map(nodes.map(node => [node.id, node]));
  const outgoingEdgesBySource = edges.reduce((acc, edge) => {
    if (!acc.has(edge.source)) acc.set(edge.source, []);
    acc.get(edge.source).push(edge);
    return acc;
  }, new Map());

  const startNode = nodeById.get(treatmentNodeId);
  if (!startNode || !isTreatmentNode(startNode)) return [];

  const visited = new Set();
  const queue = [treatmentNodeId];
  const downstreamTreatmentNodeIds = [];

  while (queue.length) {
    const currentNodeId = queue.shift();
    if (!currentNodeId || visited.has(currentNodeId)) continue;

    visited.add(currentNodeId);

    const currentNode = nodeById.get(currentNodeId);
    if (!currentNode) continue;
    if (isTreatmentNode(currentNode)) {
      downstreamTreatmentNodeIds.push(currentNodeId);
    }

    const outgoingEdges = outgoingEdgesBySource.get(currentNodeId) ?? [];

    const nextEdges = isTreatmentNode(currentNode)
      ? outgoingEdges.filter(
          edge => edge.type === EDGE_TYPES.DISCHARGE || edge.sourceHandle === HANDLE_IDS.DISCHARGE_OUT,
        )
      : outgoingEdges;

    nextEdges.forEach(edge => queue.push(edge.target));
  }

  return downstreamTreatmentNodeIds;
};
